import { type ComponentType, type FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Heading1,
    Heading2,
    Stack,
} from '@myphx-engineering/phx-design-system';

import {
    useFillPathway,
    useFillPrescription,
} from '../../../contexts/fill/use-fill-prescription';
import { useDrugKey } from '../../../hooks/use-drug-key';
import { useFlags } from '../../../hooks/use-flags';
import { usePathwayOptions } from '../../../hooks/use-pathway-options';
import { useProgramTerms } from '../../../hooks/use-program-terms';
import type { PathSelectionV2Flag } from '../../../types/non-boolean-flags';
import { ShortProgramTerms } from '../../programs/ShortProgramTerms';

import { InsuranceCoverageDescription } from './insurance/InsuranceCoverageDescription';
import { InsurancePathSelectionCards } from './insurance/InsurancePathSelectionCards';
import { PathSelectionScreenV2 } from './PathSelectionScreenV2';
import { SelfPayCoverageDescription } from './self-pay/SelfPayCoverageDescription';
import { SelfPayPathSelectionCards } from './self-pay/SelfPayPathSelectionCards';

const PathSelectionScreenV1: FC = () => {
    const { t } = useTranslation('lilly');

    const { medication } = useFillPrescription();
    const { pathway } = useFillPathway();

    const drugKey = useDrugKey(medication);

    const selfPayTerms = useProgramTerms(medication, 'self-pay');
    const insuranceTerms = useProgramTerms(medication, 'insurance');

    let CoverageDescription: ComponentType = Fragment,
        PathSelectionCards: ComponentType = Fragment;

    switch (pathway) {
        case 'self-pay':
            CoverageDescription = SelfPayCoverageDescription;
            PathSelectionCards = SelfPayPathSelectionCards;
            break;
        case 'insurance':
            CoverageDescription = InsuranceCoverageDescription;
            PathSelectionCards = InsurancePathSelectionCards;
            break;
        case 'fallback':
            break;
    }

    return (
        <Stack gap="lg" data-testid="path-select-page-root">
            <Heading1 data-testid="path-select-title">
                {t('fill.pathSelect.title')}
            </Heading1>
            <CoverageDescription />
            <Heading2 data-testid="path-select-subtitle">
                {t('fill.pathSelect.subtitle')}
            </Heading2>
            <PathSelectionCards />
            <ShortProgramTerms
                drugKey={drugKey}
                pathway="insurance"
                terms={insuranceTerms}
            />
            <ShortProgramTerms
                drugKey={drugKey}
                pathway="self-pay"
                terms={selfPayTerms}
            />
        </Stack>
    );
};

export const PathSelectionScreen: FC = () => {
    const { medication } = useFillPrescription();
    const { enablePathSelectionV2 } = useFlags();

    const drugKey = useDrugKey(medication);
    const pathwayOptions = usePathwayOptions();

    const pathSelectionConfig = enablePathSelectionV2 as PathSelectionV2Flag;

    const useV2 =
        !!pathwayOptions ||
        (!!drugKey && pathSelectionConfig.drugKeys.includes(drugKey));

    if (useV2) {
        return <PathSelectionScreenV2 />;
    }

    return <PathSelectionScreenV1 />;
};
